import FormField from "components/FormField";
import MDModal from "components/MDModal";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";
import { useCreateAccount, useFreeAccountTemplatesPicker } from "queries/accounts";
import SoftMultiSelect from "components/SoftMultiSelect";
import Grid from "@mui/material/Grid";
import { useAgencyInfo } from "context/agency";
import Select from "react-select"; // Import react-select
import TagsSelect from "./TagsSelect";
import ModelPromptslabels from "../../../UiPrompts/promptlabels/ModelPrompts/Create";
import { CircularProgress, Switch, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const { GET, POST } = ReqMethods;

const timezones = [
  { value: "Pacific/Midway", label: "(GMT-11:00) Midway Island" },
  { value: "Pacific/Niue", label: "(GMT-11:00) Niue" },
  { value: "Pacific/Pago_Pago", label: "(GMT-11:00) Pago Pago" },
  { value: "America/Adak", label: "(GMT-10:00) Alaska" },
  { value: "America/Los_Angeles", label: "(GMT-08:00) Pacific Time (US & Canada)" },
  { value: "America/Denver", label: "(GMT-07:00) Mountain Time (US & Canada)" },
  { value: "America/Chicago", label: "(GMT-06:00) Central Time (US & Canada)" },
  { value: "America/New_York", label: "(GMT-05:00) Eastern Time (US & Canada)" },
  { value: "America/Halifax", label: "(GMT-04:00) Atlantic Time (Canada)" },
  { value: "America/Toronto", label: "(GMT-04:00) Eastern Time (Canada)" },
  { value: "Europe/London", label: "(GMT+00:00) London" },
  { value: "Europe/Berlin", label: "(GMT+01:00) Berlin" },
  { value: "Europe/Moscow", label: "(GMT+03:00) Moscow" },
  { value: "Europe/Paris", label: "(GMT+01:00) Paris" },
  { value: "Europe/Rome", label: "(GMT+01:00) Rome" },
  { value: "Europe/Madrid", label: "(GMT+01:00) Madrid" },
  { value: "Europe/Amsterdam", label: "(GMT+01:00) Amsterdam" },
  { value: "Europe/Brussels", label: "(GMT+01:00) Brussels" },
  { value: "Asia/Tokyo", label: "(GMT+09:00) Tokyo" },
  { value: "Asia/Shanghai", label: "(GMT+08:00) Shanghai" },
  { value: "Asia/Hong_Kong", label: "(GMT+08:00) Hong Kong" },
  { value: "Asia/Singapore", label: "(GMT+08:00) Singapore" },
  { value: "Asia/Seoul", label: "(GMT+09:00) Seoul" },
  { value: "Australia/Sydney", label: "(GMT+10:00) Sydney" },
  { value: "Australia/Melbourne", label: "(GMT+10:00) Melbourne" },
  { value: "Australia/Brisbane", label: "(GMT+10:00) Brisbane" },
  { value: "Pacific/Auckland", label: "(GMT+13:00) Auckland" },
  { value: "Pacific/Fiji", label: "(GMT+12:00) Fiji" },
  { value: "America/Argentina/Buenos_Aires", label: "(GMT-03:00) Buenos Aires" },
  { value: "America/Bogota", label: "(GMT-05:00) Bogota" },
  { value: "America/Santiago", label: "(GMT-03:00) Santiago" },
  { value: "Asia/Bangkok", label: "(GMT+07:00) Bangkok" },
  { value: "Asia/Calcutta", label: "(GMT+05:30) Kolkata" },
  { value: "Asia/Dubai", label: "(GMT+04:00) Dubai" },
  { value: "Asia/Jerusalem", label: "(GMT+02:00) Jerusalem" },
  { value: "Asia/Kabul", label: "(GMT+04:30) Kabul" },
  { value: "Africa/Cairo", label: "(GMT+02:00) Cairo" },
  { value: "Africa/Nairobi", label: "(GMT+03:00) Nairobi" },
  { value: "Africa/Lagos", label: "(GMT+01:00) Lagos" },
  { value: "America/Caracas", label: "(GMT-04:00) Caracas" },
  { value: "America/Guayaquil", label: "(GMT-05:00) Guayaquil" },
  { value: "America/Phoenix", label: "(GMT-07:00) Phoenix" },
  { value: "America/Indiana/Indianapolis", label: "(GMT-05:00) Indianapolis" },
  { value: "Pacific/Apia", label: "(GMT+13:00) Apia" },
  { value: "Pacific/Chatham", label: "(GMT+13:45) Chatham Islands" },
  { value: "America/Managua", label: "(GMT-06:00) Managua" },
  { value: "America/Guatemala", label: "(GMT-06:00) Guatemala" },
  { value: "America/Tegucigalpa", label: "(GMT-06:00) Tegucigalpa" },
  { value: "America/Asuncion", label: "(GMT-04:00) Asuncion" },
  { value: "America/Montevideo", label: "(GMT-03:00) Montevideo" },
  { value: "Pacific/Port_Moresby", label: "(GMT+10:00) Port Moresby" },
  { value: "Pacific/Guam", label: "(GMT+10:00) Guam" },
  { value: "America/Belize", label: "(GMT-06:00) Belize" },
  { value: "America/Santo_Domingo", label: "(GMT-04:00) Santo Domingo" },
  { value: "America/Puerto_Rico", label: "(GMT-04:00) Puerto Rico" },
  { value: "America/Costa_Rica", label: "(GMT-06:00) Costa Rica" },
  { value: "America/Chicago", label: "(GMT-06:00) Central Time (US & Canada)" },
  { value: "America/Los_Angeles", label: "(GMT-08:00) Pacific Time (US & Canada)" },
  { value: "America/New_York", label: "(GMT-05:00) Eastern Time (US & Canada)" },
];

export function ModalComponent({
  open,
  onClose,
  products,
  apps,
  handleRefresh,
  title,
  templateData,
}) {
  const request = useRequest();
  const [agency] = useAgencyInfo();
  const queryClient = useQueryClient();
  const [openWarningModel, setOpenWarningModel] = useState(false);
  const [selectedApp, setSelectedApp] = useState("");
  const [selectedTimezone, setSelectedTimezone] = useState(null); // State for timezone selection
  const [errors, setErrors] = useState({});
  const [tags, setTags] = useState([]);
  const [addTagModel, setAddTagModel] = useState(false);
  const [createAccounts, setCreateAccounts] = useState(false);
  const [addTeamMember, setAddTeamMember] = useState(false);
  const [customAIAgent, setCustomAIAgent] = useState(false);
  const [unlimitedAgents, setUnlimitedAgents] = useState(false);
  const [unlimitedTasks, setUnlimitedTasks] = useState(false);

  // Prefill state from template when provided
  useEffect(() => {
    if (templateData) {
      const hasYourAI = Array.isArray(templateData.apps)
        ? templateData.apps.includes("Your AI")
        : templateData.apps === "Your AI";
      setSelectedApp(hasYourAI ? "Your AI" : "");
      setTags(Array.isArray(templateData.promptlabels_ids) ? templateData.promptlabels_ids : []);
      setCreateAccounts(!!templateData.create_additional_workspaces);
      setAddTeamMember(!!templateData.can_add_team_members);
      setCustomAIAgent(!!templateData.can_build_custom_ai_agents);
      setUnlimitedAgents(!!templateData.unlimited_ai_agents);
      setUnlimitedTasks(!!templateData.unlimited_tasks);
    } else {
      setSelectedApp("");
      setTags([]);
      setCreateAccounts(false);
      setAddTeamMember(false);
      setCustomAIAgent(false);
      setUnlimitedAgents(false);
      setUnlimitedTasks(false);
    }
  }, [templateData, open]);

  function generateRandomID(length) {
    let result = "";
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength = characters.length;

    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }

    return result;
  }
  const handleChangeTags = (selectedOptions) => {
    setTags(selectedOptions ? selectedOptions?.map((option) => option.value) : []);
  };

  // saas domain - not an Accounts hook (another fork owns queries/saas.js
  // territory), so inlined directly via useRequest() rather than centralized.
  const { data: products_data = null } = useQuery({
    queryKey: ["agency-free-saas", agency._id],
    queryFn: async () => {
      const { response } = await request(GET, "/saas/agencysaas", {
        query: `agency_id=${agency._id}`,
        toaster: false,
      });
      if (response && response.data) {
        const freeSaasItem = response.data.filter((item) => item?.is_free_saas === true)[0];
        return freeSaasItem?._id || null;
      }
      return null;
    },
    enabled: !!agency._id && open,
  });

  const validateForm = (form) => {
    const newErrors = {};
    if (!form.email.value) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(form.email.value)) {
      newErrors.email = "Email address is invalid";
    }
    if (!form.business_name.value) {
      newErrors.business_name = "Business Name is required";
    }
    if (!selectedTimezone) {
      // Add timezone validation
      newErrors.time_zone = "Time Zone is required";
    }
    return newErrors;
  };

  const { mutateAsync: createAccount, isPending: accountLoading } = useCreateAccount();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm(e.target);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    const payload = {
      id: generateRandomID(20),
      first_name: "",
      last_name: "",
      email: e.target.email.value,
      business_name: e.target.business_name.value,
      address: e.target.address.value,
      city: e.target.city.value,
      country: e.target.country.value,
      state: e.target.state.value,
      zip_code: e.target.zip_code.value,
      phone_number: e.target.phone_number.value,
      time_zone: selectedTimezone?.value, // Use selected timezone
      agency_id: agency._id,
      type: "free",
      apps: selectedApp,
      promptlabels_ids: tags,
      plan_id: agency.plan_id,
      number_of_additional_accounts: parseInt(e.target.number_of_additional_accounts?.value) || 0,
      number_of_team_member: parseInt(e.target.number_of_team_member?.value) || 0,
      unlimited_tasks: unlimitedTasks,
      number_of_tokens: unlimitedTasks ? 0 : parseInt(e.target.number_of_tokens?.value) || 0,
      unlimited_ai_agents: unlimitedAgents,
      number_of_ai_agents: !unlimitedAgents
        ? parseInt(e.target.number_of_ai_agents?.value) || 0
        : 0,
      team_size: "less_than_10"
    };

    try {
      await createAccount(payload);
      onClose();
    } catch (error) {
      window.alert(error?.message);
    }
  };
  const handleclose = () => {
    setAddTagModel(false);
  };
  // Inlined rather than queries/promptLabels.js's useCreatePromptLabel - that
  // hook doesn't expose a toaster option, and this call site needs
  // toaster:true preserved exactly as before.
  const createPromptLabel = (formData) =>
    request(POST, "/promptslabel/create", {
      payload: { ...formData, agency_id: agency._id, type: "agency" },
      toaster: true,
    }).then((result) => {
      queryClient.invalidateQueries({ queryKey: ["prompt-labels"] });
      return result;
    });
  const savedataPromptLabel = async (formData) => {
    const { response } = await createPromptLabel(formData);
    if (response) {
      handleclose();
      setAddTagModel(false);
    }
  };
  const handleAddTag = () => {
    setAddTagModel(true);
  };

  return (
    <>
      <MDModal open={openWarningModel} onClose={() => setOpenWarningModel(false)}>
        Already Reached your Limits to save Accounts
      </MDModal>
      {open && (
        <div className="modal-zz-confirm text-left">
          <div
            className="scrollable-modal-content bg-white rounded"
            style={{ position: "relative" }}
          >
            <h2 className="text-black text-left font-14"> {title || "Create Account"} </h2>
            <span className="crossison" onClick={onClose}>
              X
            </span>
            <SoftBox component="form" onSubmit={handleSubmit} key={templateData?._id || "fresh"}>
              <Grid container spacing={0} overflow={"auto"}>
                <Grid item xs={12}>
                  <SoftBox className="mt-2">
                    <label className="text-black">Business Name</label>
                    <input
                      className="form-control-agust"
                      placeholder="Business Name"
                      name="business_name"
                    />
                    {errors.business_name && (
                      <span style={{ color: "red" }}>{errors.business_name}</span>
                    )}
                  </SoftBox>
                </Grid>
                <Grid item xs={6} paddingRight={1}>
                  <SoftBox className="mt-2">
                    <label className="text-black">Email</label>
                    <input className="form-control-agust" placeholder="Email" name="email" />
                    {errors.email && <span style={{ color: "red" }}>{errors.email}</span>}
                  </SoftBox>
                </Grid>
                <Grid item xs={6} paddingLeft={1}>
                  <SoftBox className="mt-2">
                    <label className="text-black">Phone Number</label>
                    <input
                      className="form-control-agust"
                      placeholder="Phone Number"
                      name="phone_number"
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12}>
                  <SoftBox className="mt-2">
                    <label className="text-black">Address</label>
                    <input className="form-control-agust" placeholder="Address" name="address" />
                  </SoftBox>
                </Grid>
                <Grid item xs={6} paddingRight={1}>
                  <SoftBox className="mt-2 margin-top-10px">
                    <input className="form-control-agust" placeholder="City" name="city" />
                  </SoftBox>
                </Grid>
                <Grid item xs={6} paddingLeft={1}>
                  <SoftBox className="mt-2 margin-top-10px">
                    <input className="form-control-agust" placeholder="Country" name="country" />
                  </SoftBox>
                </Grid>
                <Grid item xs={6} paddingRight={1}>
                  <SoftBox className="mt-2 margin-top-10px">
                    <input className="form-control-agust" placeholder="State" name="state" />
                  </SoftBox>
                </Grid>
                <Grid item xs={6} paddingLeft={1}>
                  <SoftBox className="mt-2 margin-top-10px">
                    <input className="form-control-agust" placeholder="ZIP Code" name="zip_code" />
                  </SoftBox>
                </Grid>
                <Grid item xs={12}>
                  <SoftBox className="mt-2 margin-top-10px">
                    <Select
                      value={selectedTimezone}
                      onChange={setSelectedTimezone}
                      options={timezones}
                      placeholder="Select Time Zone"
                      isSearchable
                    />
                    {errors.time_zone && <span style={{ color: "red" }}>{errors.time_zone}</span>}
                  </SoftBox>
                </Grid>
                <Grid item xs={12} style={{ paddingTop: "6px" }}>
                  <SoftBox>
                    <label className="text-black">Select Apps</label>
                    <div className="text-left mb-4" style={{ width: "100%", marginTop: "10px" }}>
                      <div className="d-flex justify-between card_agust_row">
                        <div className="">
                          <p className="font-16 text-black">Your AI</p>
                        </div>
                        <div className=" ">
                          <Switch
                            checked={selectedApp === "Your AI" || false}
                            onChange={(e) => setSelectedApp(selectedApp !== "" ? "" : "Your AI")}
                          />
                        </div>
                      </div>
                      {selectedApp !== "" && (
                        <Grid item xs={12} sx={{ marginTop: "8px" }}>
                          <TagsSelect
                            agencyId={agency._id}
                            selectedIds={tags}
                            onChange={handleChangeTags}
                          />
                        </Grid>
                      )}
                    </div>
                  </SoftBox>
                </Grid>

                <div className="text-left mb-4" style={{ width: "100%", marginTop: "10px" }}>
                  <div className="d-flex justify-between card_agust_row">
                    <div className="">
                      <p className="font-16 text-black">Create additional accounts</p>
                      <p className="font-15">Can this account create additional accounts?</p>
                    </div>
                    <div className=" ">
                      <Switch
                        checked={createAccounts || false}
                        onChange={(e) => setCreateAccounts(e.target.checked)}
                      />
                    </div>
                  </div>

                  {createAccounts && (
                    <div className="text-left mb-4" style={{ width: "100%", marginTop: "10px" }}>
                      <label
                        htmlFor="number_of_additional_accounts"
                        className="block text-black mb-2"
                        style={{ marginLeft: "4px" }}
                      >
                        Number of additional accounts
                      </label>
                      <p className="font-15 mb-2" style={{ marginLeft: "4px" }}>
                        How many additional accounts can this account create
                      </p>
                      <input
                        type="number"
                        id="number_of_additional_accounts"
                        className="form-control-agust w-full"
                        name="number_of_additional_accounts"
                        placeholder="Enter number of additional accounts"
                        min="0"
                        defaultValue={templateData?.number_of_additional_workspaces || 0}
                      />
                    </div>
                  )}
                </div>
                <div className="text-left mb-4" style={{ width: "100%", marginTop: "10px" }}>
                  <div className="d-flex justify-between card_agust_row">
                    <div className="">
                      <p className="font-16 text-black">Can add team members</p>
                      {/* <p className="font-15">Can this account create additional accounts?</p> */}
                    </div>
                    <div className=" ">
                      <Switch
                        checked={addTeamMember || false}
                        onChange={(e) => setAddTeamMember(e.target.checked)}
                      />
                    </div>
                  </div>

                  {addTeamMember && (
                    <div className="text-left mb-4" style={{ width: "100%", marginTop: "10px" }}>
                      <label
                        htmlFor="number_of_additional_accounts"
                        className="block text-black mb-2"
                        style={{ marginLeft: "4px" }}
                      >
                        How many team members can be added to this account
                      </label>
                      {/* <p className="font-15 mb-2" style={{ marginLeft: "4px" }}>
                        How many additional accounts can this account create
                      </p> */}
                      <input
                        type="number"
                        id="number_of_team_member"
                        className="form-control-agust w-full"
                        name="number_of_team_member"
                        placeholder="Enter number of team members"
                        min="0"
                        defaultValue={templateData?.number_of_team_member || 0}
                      />
                    </div>
                  )}
                </div>
                <div className="text-left mb-4" style={{ width: "100%", marginTop: "10px" }}>
                  <div className="d-flex justify-between card_agust_row">
                    <div className="">
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <p className="font-16 text-black">Unlimited AI Tasks</p>
                        <Tooltip
                          title="When enabled, this account will have no monthly AI Task limit"
                          arrow
                        >
                          <InfoOutlinedIcon
                            style={{ fontSize: "16px", color: "#6B7280", cursor: "pointer" }}
                          />
                        </Tooltip>
                      </div>
                    </div>
                    <div className=" ">
                      <Switch
                        checked={unlimitedTasks || false}
                        onChange={(e) => setUnlimitedTasks(e.target.checked)}
                      />
                    </div>
                  </div>

                  {!unlimitedTasks && (
                    <div className="text-left mb-4" style={{ width: "100%", marginTop: "10px" }}>
                      <label
                        htmlFor="number_of_tokens"
                        className="block text-black mb-2"
                        style={{ marginLeft: "4px" }}
                      >
                        AI Tasks Monthly Limit
                      </label>
                      <input
                        type="number"
                        id="number_of_tokens"
                        className="form-control-agust w-full"
                        name="number_of_tokens"
                        placeholder="Number of monthly AI Tasks"
                        min="0"
                        defaultValue={templateData?.number_of_tokens || ""}
                      />
                    </div>
                  )}
                </div>
                <div className="text-left mb-4" style={{ width: "100%", marginTop: "10px" }}>
                  <div className="d-flex justify-between card_agust_row">
                    <div className="">
                      <p className="font-16 text-black">Can build Custom AI Agents</p>
                      {/* <p className="font-15">Can this account create additional accounts?</p> */}
                    </div>
                    <div className=" ">
                      <Switch
                        checked={customAIAgent || false}
                        onChange={(e) => setCustomAIAgent(e.target.checked)}
                      />
                    </div>
                  </div>

                  {customAIAgent && (
                    <div className="d-flex justify-between card_agust_row">
                      <div className="">
                        <p className="font-16 text-black">Unlimited AI Agents</p>
                        {/* <p className="font-15">Can this account create additional accounts?</p> */}
                      </div>
                      <div className=" ">
                        <Switch
                          checked={unlimitedAgents || false}
                          onChange={(e) => setUnlimitedAgents(e.target.checked)}
                        />
                      </div>
                    </div>
                  )}

                  {customAIAgent && !unlimitedAgents && (
                    <div className="text-left mb-4" style={{ width: "100%", marginTop: "10px" }}>
                      <label
                        htmlFor="number_of_additional_accounts"
                        className="block text-black mb-2"
                        style={{ marginLeft: "4px" }}
                      >
                        How many AI Agents can this plan create?
                      </label>
                      {/* <p className="font-15 mb-2" style={{ marginLeft: "4px" }}>
                        How many additional accounts can this account create
                      </p> */}
                      <input
                        type="number"
                        id="number_of_ai_agents"
                        className="form-control-agust w-full"
                        name="number_of_ai_agents"
                        placeholder="Enter number of aI Agents"
                        min="1"
                        defaultValue={templateData?.number_of_ai_agents || 1}
                      />
                    </div>
                  )}
                </div>
              </Grid>
              <SoftBox mt={4}>
                <button
                  disabled={accountLoading}
                  className="savebtn-setting w-100"
                  type="submit"
                  color="dark"
                  fullWidth
                >
                  {accountLoading ? " Adding..." : " Add"}
                </button>
              </SoftBox>
            </SoftBox>
          </div>
        </div>
      )}

      {addTagModel && (
        <ModelPromptslabels
          data={null}
          modelfunctionality={savedataPromptLabel}
          openModel={addTagModel}
          onclosemodel={handleclose}
        />
      )}
    </>
  );
}

ModalComponent.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleRefresh: PropTypes.func,
  products: PropTypes.array,
  apps: PropTypes.array,
  title: PropTypes.string,
  templateData: PropTypes.object,
};

/**
 * Step 1: Ask "Create account from a template?" — Yes/No + template dropdown + Continue.
 */
function TemplateChooserModal({ open, onClose, templates, isTemplatesLoading, onContinue }) {
  const [useTemplate, setUseTemplate] = useState(null); // "yes" | "no" | null
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  useEffect(() => {
    if (open) {
      setUseTemplate(null);
      setSelectedTemplateId("");
    }
  }, [open]);

  if (!open) return null;

  const canContinue =
    useTemplate === "no" || (useTemplate === "yes" && selectedTemplateId);

  const handleContinue = () => {
    if (!canContinue) return;
    const selected =
      useTemplate === "yes"
        ? templates.find((t) => t._id === selectedTemplateId) || null
        : null;
    onContinue(selected);
  };

  const yesBtnStyle = {
    flex: 1,
    padding: "14px 20px",
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "14px",
    cursor: "pointer",
    backgroundColor: useTemplate === "yes" ? "black" : "white",
    color: useTemplate === "yes" ? "white" : "#111827",
    border: useTemplate === "yes" ? "1px solid black" : "1px solid #E5E5E5",
  };

  const noBtnStyle = {
    flex: 1,
    padding: "14px 20px",
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "14px",
    cursor: "pointer",
    backgroundColor: useTemplate === "no" ? "black" : "white",
    color: useTemplate === "no" ? "white" : "#111827",
    border: useTemplate === "no" ? "1px solid black" : "1px solid #E5E5E5",
  };

  return (
    <div className="modal-zz-confirm text-left">
      <div
        className="scrollable-modal-content bg-white rounded"
        style={{ position: "relative", maxWidth: "720px", width: "720px" }}
      >
        <h2 className="text-black text-left font-14"> Create Account </h2>
        <p className="font-15" style={{ marginTop: "4px" }}>
          Create account from a template?
        </p>
        <span className="crossison" onClick={onClose}>
          X
        </span>

        <div style={{ display: "flex", gap: "16px", marginTop: "16px" }}>
          <button type="button" style={yesBtnStyle} onClick={() => setUseTemplate("yes")}>
            Yes
          </button>
          <button type="button" style={noBtnStyle} onClick={() => setUseTemplate("no")}>
            No
          </button>
        </div>

        {useTemplate === "yes" && (
          <div style={{ marginTop: "20px" }}>
            <label className="text-black">Select a template</label>
            {isTemplatesLoading ? (
              <div
                style={{
                  width: "100%",
                  marginTop: "6px",
                  height: "44px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "0 12px",
                  border: "1px solid #E5E5E5",
                  borderRadius: "8px",
                }}
              >
                <CircularProgress size={18} />
                <span style={{ fontSize: "14px", color: "#626262" }}>Loading templates...</span>
              </div>
            ) : (
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                style={{
                  width: "100%",
                  marginTop: "6px",
                  height: "44px",
                  padding: "0 12px",
                  fontSize: "14px",
                  border: "1px solid #E5E5E5",
                  borderRadius: "8px",
                  backgroundColor: "white",
                  outline: "none",
                  cursor: "pointer",
                }}
              >
                <option value="">Select a template</option>
                {templates.map((t) => (
                  <option key={t._id} value={t._id}>
                    {t.template_name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <SoftBox mt={4}>
          <button
            type="button"
            disabled={!canContinue}
            onClick={handleContinue}
            style={{
              width: "100%",
              padding: "14px",
              backgroundColor: "black",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "14px",
              opacity: canContinue ? 1 : 0.55,
              cursor: canContinue ? "pointer" : "not-allowed",
            }}
          >
            Continue
          </button>
        </SoftBox>
      </div>
    </div>
  );
}

TemplateChooserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  templates: PropTypes.array,
  isTemplatesLoading: PropTypes.bool,
  onContinue: PropTypes.func.isRequired,
};

function Modal({ products, apps, handleRefresh }) {
  const [agency] = useAgencyInfo();
  const [step, setStep] = useState("closed"); // "closed" | "chooser" | "create"
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const { data: templates = [], isLoading: isTemplatesLoading } = useFreeAccountTemplatesPicker(
    agency?._id,
    step === "chooser",
  );

  const handleOpen = () => {
    setSelectedTemplate(null);
    setStep("chooser");
  };

  const handleClose = () => {
    setStep("closed");
    setSelectedTemplate(null);
  };

  const handleChooserContinue = (template) => {
    setSelectedTemplate(template);
    setStep("create");
  };

  return (
    <>
      <button className="btn btn-outline-dark" onClick={handleOpen}>
        Create Account
      </button>

      <TemplateChooserModal
        open={step === "chooser"}
        onClose={handleClose}
        templates={templates}
        isTemplatesLoading={isTemplatesLoading}
        onContinue={handleChooserContinue}
      />

      <ModalComponent
        open={step === "create"}
        onClose={handleClose}
        products={products}
        apps={apps}
        handleRefresh={handleRefresh}
        templateData={selectedTemplate}
      />
    </>
  );
}

Modal.propTypes = {
  products: PropTypes.array,
  handleRefresh: PropTypes.func,
  apps: PropTypes.array,
};

export default Modal;
